import {
  DAILY_MARKET_ASK_ED_DEFAULT_USER_QUESTION,
  formatDailyMarketReportDate,
} from "@hooks/dailyMarket/buildDailyMarketAskEdQuestion";

/** Protocol: ask-ed-integration-protocol.md §2/§4 — referenceSource **encode 后** ≤ 1000 字符 */
export const REPORT_ASK_ED_MAX_REFERENCE_SOURCE_CHARS = 1000;

/** 默认问题（英文逐字，translate 待补清单机制） */
export const REPORT_ASK_ED_SIGNAL_DEFAULT_QUESTION =
  "What should I know about this signal?";
export const REPORT_ASK_ED_GOAL_DEFAULT_QUESTION =
  "What should I know about this goal?";

export function encodeReportReferenceSource(value: string): string {
  // encodeURIComponent leaves ( ) unescaped; protocol examples use %28/%29 inside markdown links.
  return encodeURIComponent(value).replace(/\(/g, "%28").replace(/\)/g, "%29");
}

/**
 * 按 **encode 后长度** 截断原文，避免中文膨胀冲破协议上限；
 * 切点避开 UTF-16 代理对中间，防止 encodeURIComponent 抛 URIError。
 */
export function truncateReferenceSource(source: string): string {
  const max = REPORT_ASK_ED_MAX_REFERENCE_SOURCE_CHARS;
  if (encodeReportReferenceSource(source).length <= max) {
    return source;
  }

  const ellipsis = "...";
  const ellipsisEncodedLen = encodeReportReferenceSource(ellipsis).length;
  const budget = Math.max(0, max - ellipsisEncodedLen);

  let lo = 0;
  let hi = source.length;
  let best = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    let cut = mid;
    if (cut > 0) {
      const unit = source.charCodeAt(cut - 1);
      // high surrogate → 退一格，避免 lone surrogate
      if (unit >= 0xd800 && unit <= 0xdbff) {
        cut -= 1;
      }
    }
    const encodedLen = encodeReportReferenceSource(source.slice(0, cut)).length;
    if (encodedLen <= budget) {
      best = cut;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }

  if (best <= 0) {
    return ellipsis;
  }
  return `${source.slice(0, best)}${ellipsis}`;
}

/** direct_context 协议行组装（§1）：`[方括号文案](referenceType=direct_context&referenceSource=<encoded>)\n{问题}` */
export function buildReportDirectContextQuestion(
  bracketLabel: string,
  referenceSource: string,
  userQuestion: string,
): string {
  return `[${bracketLabel}](referenceType=direct_context&referenceSource=${encodeReportReferenceSource(
    truncateReferenceSource(referenceSource),
  )})\n${userQuestion}`;
}

/** signal 页（tracker 汇报页）场景 B 形态 */
export function buildReportSignalAskEdQuestion(input: {
  cardTitle: string;
  /** ISO 串或 yyyy-MM-dd（取前 10 位作 Date 行） */
  date?: string;
  content?: string;
  userQuestion?: string;
}): string {
  const lines = [
    "Signal Type: Tracker signal",
    `Card Title: ${input.cardTitle}`,
  ];
  if (input.date) {
    lines.push(`Date: ${input.date.slice(0, 10)}`);
  }
  const content = input.content?.trim();
  if (content) {
    lines.push("", "Report Content:", content);
  }
  return buildReportDirectContextQuestion(
    input.cardTitle,
    lines.join("\n"),
    input.userQuestion?.trim() || REPORT_ASK_ED_SIGNAL_DEFAULT_QUESTION,
  );
}

/** goal 页场景 D 形态 */
export function buildReportGoalAskEdQuestion(input: {
  goalTitle: string;
  planNames?: string[];
  userQuestion?: string;
}): string {
  const lines = ["Page: Goal detail", `Card Title: ${input.goalTitle}`];
  const plans = (input.planNames ?? [])
    .map((name) => name.trim())
    .filter(Boolean);
  if (plans.length > 0) {
    lines.push(`Plan: ${plans.join(", ")}`);
  }
  return buildReportDirectContextQuestion(
    input.goalTitle,
    lines.join("\n"),
    input.userQuestion?.trim() || REPORT_ASK_ED_GOAL_DEFAULT_QUESTION,
  );
}

/** 市场总结页场景 D 通用形态（页面主路径走同事 buildDailyMarketAskEdQuestion；此处供 useReportAskEd 市场分支） */
export function buildReportMarketAskEdQuestion(input: {
  title: string;
  /** yyyyMMdd（同事 formatDailyMarketReportDate 同源格式化） */
  date?: string;
  userQuestion?: string;
}): string {
  const lines = ["Page: Daily market summary", `Card Title: ${input.title}`];
  if (input.date) {
    lines.push(`Date: ${formatDailyMarketReportDate(input.date)}`);
  }
  return buildReportDirectContextQuestion(
    input.title,
    lines.join("\n"),
    input.userQuestion?.trim() || DAILY_MARKET_ASK_ED_DEFAULT_USER_QUESTION,
  );
}

/**
 * goal 信号页 Ask Ed（brief_card 引用形态）：协议 §3 简单判断规则——后端可用 recordId
 * 查到 tracker_record 全量内容，无需 direct_context 内嵌 referenceSource（1000 字符截断）。
 * 只预填引用链接、不带默认问题，由用户手动输入（与 account read 不代发口径一致）。
 */
export function buildGoalSignalBriefCardQuestion(input: {
  signalId: string;
  signalTitle?: string;
}): string {
  const label = input.signalTitle?.trim() || "Goal signal";
  return `[${label}](referenceType=brief_card&referenceId=${encodeReportReferenceSource(
    input.signalId,
  )})`;
}
